import React, { useCallback, useMemo, useState } from 'react'
import { Box, Popover, Tooltip, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import { Icon } from '@iconify/react'
import styled from 'styled-components'
import {
  MilestoneDetail,
  MilestoneStatus,
} from '../../../../../../../../domain/entity/MilestoneEntity'
import { colorPalette } from '../../../../../../../style/colorPalette'
import BigFireIcon from '../../../../../../../components/icons/BigFireIcon'
import { intl } from '../../../../../../../../i18n'

export type MilestoneWithPosition = {
  milestone: MilestoneDetail
  position?: number
}

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  position: relative;
  line-height: 1;
`

const PopoverContent = styled.div`
  padding: 8px 12px;
`

const PopoverItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const PopoverItemWrapper = styled.div<{ $marginTop?: number }>`
  margin-top: ${({ $marginTop }) => $marginTop ?? 0}px;
`

const ClickableSpan = styled.span`
  cursor: pointer;
`

type PositionedMilestoneIconProps = {
  $position: number
}

const PositionedMilestoneIcon = styled.span<PositionedMilestoneIconProps>`
  position: absolute;
  left: ${({ $position }) => $position}%;
  transform: translateX(-50%);
  cursor: pointer;
`

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.green[5]};
`

const AchievedBadge = styled(CheckCircleIcon)`
  position: absolute;
  top: -3px;
  right: -2px;
  font-size: 14px;
  color: ${colorPalette.green[5]};
  background-color: ${colorPalette.monotone[0]};
  border-radius: 50%;
`

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)`
  font-size: 14px;
  flex-shrink: 0;
  color: ${colorPalette.monotone[4]};
`

const DateTypography = styled(Typography)`
  font-size: 11px;
  color: ${colorPalette.monotone[5]};
  white-space: nowrap;
`

const NameTypography = styled(Typography)`
  font-size: 12px;
  flex: 1;
  min-width: 0;
`

const ClickableName = styled(Typography)`
  font-size: 12px;
  flex: 1;
  min-width: 0;
  color: ${colorPalette.skyBlue[7]};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const EmptyCellContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const ButtonWrapper = styled(Box)`
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddButton = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${colorPalette.skyBlue[1]};
  color: ${colorPalette.skyBlue[7]};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${colorPalette.skyBlue[2]};
  }
`

const SmallAddIcon = styled(AddIcon)`
  font-size: 15px;
`

const popoverProps = {
  anchorOrigin: { vertical: 'center' as const, horizontal: 'right' as const },
  transformOrigin: { vertical: 'center' as const, horizontal: 'left' as const },
  sx: { marginLeft: '4px' },
}

export const getMilestoneStatus = (
  milestone: MilestoneDetail
): MilestoneStatus => {
  if (milestone.achieved) return 'achieved'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const milestoneDate = new Date(milestone.milestoneDate)
  if (milestoneDate < today) return 'overdue'
  return 'pending'
}

const formatMilestoneDate = (dateString: string): string => {
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

const StatusIcon = ({ milestone }: { milestone: MilestoneDetail }) => {
  const status = getMilestoneStatus(milestone)

  if (status === 'achieved') {
    return <StyledCheckCircleIcon />
  }
  if (status === 'overdue') {
    return <BigFireIcon style={{ fontSize: 14, flexShrink: 0 }} />
  }
  return <StyledCheckCircleOutlineIcon />
}

const MilestoneIconWithBadge = ({
  milestone,
}: {
  milestone: MilestoneDetail
}) => (
  <IconContainer>
    <Icon
      icon={milestone.iconName ?? 'mdi:cards-diamond'}
      width={20}
      height={20}
      color={milestone.color}
    />
    {milestone.achieved ? <AchievedBadge /> : null}
  </IconContainer>
)

type MilestonePopoverContentProps = {
  milestones: MilestoneDetail[]
  showDate?: boolean
  onEdit?: (uuid: string) => void
}

const MilestonePopoverItem = ({
  milestone,
  showDate,
  onEdit,
}: {
  milestone: MilestoneDetail
  showDate?: boolean
  onEdit?: (uuid: string) => void
}) => {
  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(milestone.uuid)
  }

  return (
    <PopoverItem>
      {showDate && (
        <DateTypography variant="body2">
          {formatMilestoneDate(milestone.milestoneDate)}
        </DateTypography>
      )}
      {onEdit ? (
        <ClickableName variant="body2" onClick={handleNameClick}>
          {milestone.name}
        </ClickableName>
      ) : (
        <NameTypography variant="body2">{milestone.name}</NameTypography>
      )}
      <StatusIcon milestone={milestone} />
    </PopoverItem>
  )
}

const MilestonePopoverContent = ({
  milestones,
  showDate = false,
  onEdit,
}: MilestonePopoverContentProps) => (
  <PopoverContent>
    {milestones.map((milestone, index) => (
      <PopoverItemWrapper key={milestone.uuid} $marginTop={index > 0 ? 4 : 0}>
        <MilestonePopoverItem
          milestone={milestone}
          showDate={showDate}
          onEdit={onEdit}
        />
      </PopoverItemWrapper>
    ))}
  </PopoverContent>
)

export const MilestoneIcons = ({
  milestones,
  positioned = false,
  onEdit,
}: {
  milestones: MilestoneWithPosition[]
  positioned?: boolean
  onEdit?: (uuid: string) => void
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleEdit = useCallback(
    (uuid: string) => {
      setAnchorEl(null)
      onEdit?.(uuid)
    },
    [onEdit]
  )

  const open = Boolean(anchorEl)

  const allMilestones = milestones.map(item => item.milestone)
  const firstMilestone = milestones[0].milestone

  const groupedByPosition = useMemo(() => {
    if (!positioned) return []
    const groups = new Map<number, MilestoneDetail[]>()
    for (const { milestone, position } of milestones) {
      const pos = position ?? 50
      if (!groups.has(pos)) {
        groups.set(pos, [])
      }
      groups.get(pos)!.push(milestone)
    }
    return Array.from(groups.entries()).map(([pos, items]) => ({
      position: pos,
      milestones: items,
    }))
  }, [milestones, positioned])

  const popover = (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      {...popoverProps}
    >
      <MilestonePopoverContent
        milestones={allMilestones}
        showDate={positioned}
        onEdit={onEdit ? handleEdit : undefined}
      />
    </Popover>
  )

  if (!positioned) {
    return (
      <>
        <ClickableSpan onClick={handleClick}>
          <MilestoneIconWithBadge milestone={firstMilestone} />
        </ClickableSpan>
        {popover}
      </>
    )
  }

  return (
    <>
      {groupedByPosition.map(({ position, milestones: groupMilestones }) => {
        const groupFirstMilestone = groupMilestones[0]
        return (
          <PositionedMilestoneIcon
            key={`pos-${position}`}
            onClick={handleClick}
            $position={position}
          >
            <MilestoneIconWithBadge milestone={groupFirstMilestone} />
          </PositionedMilestoneIcon>
        )
      })}
      {popover}
    </>
  )
}

export const EmptyMilestoneCell = ({ onAdd }: { onAdd: () => void }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <EmptyCellContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <ButtonWrapper>
          <Tooltip
            arrow={true}
            title={intl.formatMessage({ id: 'milestone.popover.add' })}
          >
            <AddButton
              onClick={e => {
                e.stopPropagation()
                onAdd()
              }}
            >
              <SmallAddIcon />
            </AddButton>
          </Tooltip>
        </ButtonWrapper>
      )}
    </EmptyCellContainer>
  )
}

const PositionedAddButtonContainer = styled(Box)`
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: auto;
`

const HoverableCell = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;

  &:hover ${PositionedAddButtonContainer} {
    opacity: 1;
  }
`

export const AddButtonForPositionedCell = ({
  onAdd,
}: {
  onAdd: () => void
}) => (
  <HoverableCell>
    <PositionedAddButtonContainer>
      <Tooltip
        arrow={true}
        title={intl.formatMessage({ id: 'milestone.popover.add' })}
      >
        <AddButton
          onClick={e => {
            e.stopPropagation()
            onAdd()
          }}
        >
          <SmallAddIcon />
        </AddButton>
      </Tooltip>
    </PositionedAddButtonContainer>
  </HoverableCell>
)
